import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const ThemeContext = createContext({ settings: null, refresh: () => {}, save: async () => {} });

// Font catalog — admin can pick from these. All loaded in index.css.
export const HEADING_FONTS = ["Cabinet Grotesk", "Playfair Display", "Manrope", "Space Grotesk", "Inter"];
export const BODY_FONTS = ["IBM Plex Sans", "Inter", "Manrope", "Source Sans 3", "Geist"];

function relativeLuminance(hex) {
  const c = (hex || "").replace("#", "");
  if (c.length !== 6) return 0;
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;
  const lin = (v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

const DEFAULTS = {
  bg_color: "#050505",
  text_color: "#F5F5F5",
  muted_color: "#8E8E93",
  heading_color: "#FFFFFF",
  accent_color: "#FF4500",
  secondary_color: "#DFFF00",
  tertiary_color: "#00E5FF",
  font_scale: 1.0,
  font_heading: "Cabinet Grotesk",
  font_body: "IBM Plex Sans",
};

export function applyTheme(s = {}) {
  const settings = { ...DEFAULTS, ...s };
  const root = document.documentElement;
  const lum = relativeLuminance(settings.bg_color);
  const mode = lum > 0.5 ? "light" : "dark";
  root.setAttribute("data-theme", mode);
  root.style.setProperty("--ww-bg", settings.bg_color);
  root.style.setProperty("--ww-text", settings.text_color);
  root.style.setProperty("--ww-text-2", settings.muted_color);
  root.style.setProperty("--ww-heading", settings.heading_color);
  root.style.setProperty("--ww-accent", settings.accent_color);
  root.style.setProperty("--ww-volt", settings.secondary_color);
  root.style.setProperty("--ww-cyan", settings.tertiary_color);
  root.style.setProperty("--ww-font-heading", `'${settings.font_heading}', 'IBM Plex Sans', sans-serif`);
  root.style.setProperty("--ww-font-body", `'${settings.font_body}', system-ui, sans-serif`);
  root.style.setProperty("--ww-grid-line", mode === "light" ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.025)");
  root.style.fontSize = `${(settings.font_scale || 1) * 16}px`;
  return mode;
}

export function ThemeProvider({ children }) {
  const [settings, setSettings] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/site-settings");
      setSettings(data);
      applyTheme(data);
      // Cache for the anti-flicker inline script in index.html
      try { localStorage.setItem("ww_theme_cache", JSON.stringify(data)); } catch { /* quota / private mode */ }
    } catch {
      applyTheme(DEFAULTS);
    }
  }, []);

  const save = useCallback(async (token, payload) => {
    const { data } = await api.put("/admin/site-settings", payload, {
      headers: { "X-Admin-Token": token },
    });
    setSettings(data);
    applyTheme(data);
    try { localStorage.setItem("ww_theme_cache", JSON.stringify(data)); } catch { /* ignore */ }
    return data;
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <ThemeContext.Provider value={{ settings, refresh, save }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
